import { propertyof } from '@/utilities';
import type MatrixRow from './MatrixRow';
import { toplevelpropertyof } from '@/utilities/utils';

export const buildDefaultMatrixConfig = (existing: IMatrixConfig | undefined | null): IMatrixConfig => {
    // General columns have some columns that are selected by default.
    // This is every column except "TeamName", which is hidden by default
    let _generalColumns = existing?.GeneralColumns ?? [
        toplevelpropertyof<MatrixRow>('Bib'),
        toplevelpropertyof<MatrixRow>('StartTime'),
        toplevelpropertyof<MatrixRow>('ExitStatus'),
        toplevelpropertyof<MatrixRow>('ContestantName'),
        toplevelpropertyof<MatrixRow>('GroupName'),
        toplevelpropertyof<MatrixRow>('CategoryName'),
    ];

    return {
        RaceID: existing?.RaceID ?? null,
        StationRoleIDs: existing?.StationRoleIDs ?? [],
        get GeneralColumns() {
            if (this.FilterHideDNX) {
                // If we arent showing DNX, then we shouldnt show the exit status column either
                return _generalColumns.filter((col) => col !== propertyof<MatrixRow>('ExitStatus'));
            }
            return _generalColumns;
        },
        set GeneralColumns(value: Array<keyof MatrixRow>) {
            _generalColumns = value;
        },
        RaceGroupIDs: existing?.RaceGroupIDs ?? [],
        CategoryIDs: existing?.CategoryIDs ?? [],
        FilterToMissingTimes: existing?.FilterToMissingTimes ?? false,
        FilterToInTrack: existing?.FilterToInTrack ?? false,
        FilterToWarnings: existing?.FilterToWarnings ?? false,
        FilterHideDNX: existing?.FilterHideDNX ?? false,
        ShowClockTime: existing?.ShowClockTime ?? false,
        ShowTimeAsX: existing?.ShowTimeAsX ?? false,
        TimeDecimals: existing?.TimeDecimals ?? 'TIME',
        SpeedLimitKph: existing?.SpeedLimitKph ?? 0,
        SearchString: existing?.SearchString ?? '',
        ShowGpiStartWarning: existing?.ShowGpiStartWarning ?? true,
        DeviceStatusVisible: existing?.DeviceStatusVisible ?? true,
        BiathlonPicuresSearchWindowSecondsStart: existing?.BiathlonPicuresSearchWindowSecondsStart ?? 10,
        BiathlonPicuresSearchWindowSecondsEnd: existing?.BiathlonPicuresSearchWindowSecondsEnd ?? 60,
        HideBiathlonPictures: existing?.HideBiathlonPictures ?? false,
    };
};

/**
 * Represents the configuration of the matrix.
 *
 * !! Note: This should only ever be built by the {@link buildDefaultMatrixConfig} function!!
 */
export default interface IMatrixConfig {
    /**
     * Selected race ID
     */
    RaceID: number | null;

    /**
     * Columns filter for stationRoles.
     */
    StationRoleIDs: number[];

    /**
     * Columns filter for general columns.
     */
    GeneralColumns: Array<keyof MatrixRow>;

    /**
     * Filter rows to groups.
     */
    RaceGroupIDs: number[];

    /**
     * Filter rows to categories.
     */
    CategoryIDs: number[];

    /**
     * Row filter for missing times. This works in combination with filterToInTrack and filterToWarnings. If none is set, then all is shown.
     */
    FilterToMissingTimes: boolean;

    /**
     * Row filter for in track. This works in combination with filterToMissingTimes and filterToWarnings. If none is set, then all is shown.
     */
    FilterToInTrack: boolean;

    /**
     * Row filter for warnings. This works in combination with filterToMissingTimes and filterToInTrack. If none is set, then all is shown.
     */
    FilterToWarnings: boolean;

    /**
     * Row filter for DNX. If true, DNX is hidden
     */
    FilterHideDNX: boolean; // TODO: Is anyone ever interested in filtering the invert of this? Only show DNX?

    /**
     * Show the clock time or the used time
     */
    ShowClockTime: boolean;

    /**
     * How many decimals to show for times
     */
    TimeDecimals: 'TIME' | 'TIMEMS1' | 'TIMEMS2' | 'TIMEMS3';

    /**
     * Show the time as X instead of the time, the idea being that the columns will be really small
     */
    ShowTimeAsX: boolean;

    /**
     * Show warning if speed excceds the limit
     */
    SpeedLimitKph: number;

    /**
     * Whatever the user has typed into the search box
     */
    SearchString: string;

    /**
     * Show warning on start cells if the contestant doesnt have a GPI starttime
     */
    ShowGpiStartWarning: boolean;

    /**
     * Wether to show the device status view is collapsed or not
     */
    DeviceStatusVisible: boolean;

    /**
     * How many seconds after a shooting to start to look for biathlonpictures
     */
    BiathlonPicuresSearchWindowSecondsStart: number;

    /**
     * How many seconds after a shooting to end to look for biathlonpictures
     */
    BiathlonPicuresSearchWindowSecondsEnd: number;

    /**
     * If true, stops rendering the biathlonpictures
     */
    HideBiathlonPictures: boolean;
}
